import { Controller } from "@hotwired/stimulus"

// Desktop left-rail account menu. The panel is a position:fixed popup, but the
// rail it lives in is frosted (backdrop-filter) on the home scene — and any
// backdrop-filter ancestor becomes the containing block for fixed descendants
// AND clips them by its overflow, so the popup renders invisibly inside the
// rail. To dodge that, we reparent the panel to <body> WHILE OPEN (where
// nothing clips it) and put it back in the rail on close. Keeping the DOM
// intact when closed is what lets Turbo cache/restore the page cleanly — a
// permanent move in connect() would orphan the target on the next visit.
export default class extends Controller {
    static targets = ["trigger", "panel"]

    connect() {
        // Capture the node now, while it still lives inside the controller; once
        // reparented to <body> it leaves the target scope and hasPanelTarget
        // would go false.
        this.panel = this.panelTarget

        this.outsideClick = (event) => {
            if (this.element.contains(event.target)) return
            if (this.panel.contains(event.target)) return
            this.close()
        }
        this.keydown = (event) => {
            if (event.key === "Escape") {
                this.close()
                this.triggerTarget.focus()
            }
        }
        this.reposition = () => {
            if (!this.isOpen) return
            this.triggerTarget.getClientRects().length ? this.position() : this.close()
        }
        // Always restore the panel to the rail before Turbo snapshots the page,
        // so the cached DOM is never left with the panel stranded on <body>.
        this.beforeCache = () => this.close()
        document.addEventListener("turbo:before-cache", this.beforeCache)
        window.addEventListener("navigation:edit", this.beforeCache)
    }

    disconnect() {
        this.close()
        document.removeEventListener("turbo:before-cache", this.beforeCache)
        window.removeEventListener("navigation:edit", this.beforeCache)
    }

    get isOpen() {
        return !!this.panel && this.panel.classList.contains("is-open")
    }

    toggle(event) {
        event.preventDefault()
        this.isOpen ? this.close() : this.open()
    }

    open() {
        document.body.appendChild(this.panel)
        this.panel.classList.add("assistant-nav-account-panel--portaled")
        this.panel.classList.add("is-open")
        this.position()
        this.triggerTarget.setAttribute("aria-expanded", "true")
        document.addEventListener("click", this.outsideClick)
        document.addEventListener("keydown", this.keydown)
        window.addEventListener("resize", this.reposition)
        window.addEventListener("scroll", this.reposition, true)
        this.panel.querySelector("a, button")?.focus({ preventScroll: true })
    }

    close() {
        if (!this.panel) return
        this.panel.classList.remove("is-open")
        this.panel.classList.remove("assistant-nav-account-panel--portaled")
        // Put it back inside the rail (after the trigger) so the DOM matches
        // what the server rendered.
        if (this.panel.parentElement !== this.element) {
            this.element.appendChild(this.panel)
        }
        if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", "false")
        document.removeEventListener("click", this.outsideClick)
        document.removeEventListener("keydown", this.keydown)
        window.removeEventListener("resize", this.reposition)
        window.removeEventListener("scroll", this.reposition, true)
    }

    choose() {
        this.close()
    }

    // At the foot of the rail the menu is a drop-up: above its trigger, their
    // left edges level, as Claude's is (Pieter, 2026-10-15 - opening beside
    // it read as odd). The optional desktop dock opens above its row,
    // right-aligned. Both stay inside the viewport on short screens.
    position() {
        const rect = this.triggerTarget.getBoundingClientRect()
        const gap = 10
        const navRect = this.element.closest(".assistant-bottom-nav")?.getBoundingClientRect()
        const panelRect = this.panel.getBoundingClientRect()
        const dock = navRect && navRect.width > navRect.height
        const left = dock ? rect.right - panelRect.width : rect.left
        const top = dock ? navRect.top - panelRect.height - gap : rect.top - panelRect.height - 6
        const clamp = (value, max) => Math.max(gap, Math.min(value, max))
        this.panel.style.right = "auto"
        this.panel.style.bottom = "auto"
        this.panel.style.left = `${Math.round(clamp(left, window.innerWidth - panelRect.width - gap))}px`
        this.panel.style.top = `${Math.round(clamp(top, window.innerHeight - panelRect.height - gap))}px`
    }
}
